import { Chat, LibraryVocabulary } from '@/models.ts'
import { useTranslation } from 'react-i18next'
import { ReactNode, useEffect, useRef, useState } from 'react'
import Fuse from 'fuse.js'
import { SheetContent, SheetDescription, SheetHeader } from '@/components/ui/sheet.tsx'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { DialogTitle } from '@/components/ui/dialog.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Search, X } from 'lucide-react'
import { Input } from '@/components/ui/input.tsx'
import WordInList from '@/components/word-in-list.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import useGlobalStore from '@/useGlobalStore.ts'
import { dispatch } from '@/useBus.ts'

interface SearchResult<T> {
	title: string // only to shut up the linter
	id: number
	item: T
}

const WordInListSkeleton = ({ mode }: { mode: string }) => {
	return (
		<div className="flex justify-between items-center">
			{mode === 'full' && <Skeleton className="flex items-center justify-center mx-2 w-10 h-12" />}
			<div className="flex flex-1 active:bg-accent active:text-accent-foreground">
				<Skeleton className="flex flex-none m-2 w-11 h-12" />
				<Skeleton className="grow m-2 h-12" />
			</div>
		</div>
	)
}

const ChatSearch = ({
	chatList,
	query,
	closeSheet,
}: {
	chatList: Chat[] | null
	query: string
	closeSheet: () => void
}) => {
	const { t } = useTranslation()
	const { loadChatAsActiveChat } = useGlobalStore()
	const [fuse, setFuse] = useState<Fuse<Chat>>()
	const [filteredList, setFilteredList] = useState<SearchResult<Chat>[] | null>(null)

	useEffect(() => {
		if (query === '') {
			// @ts-ignore
			setFilteredList(chatList)
		} else {
			// @ts-ignore
			setFilteredList(fuse?.search(query))
		}
	}, [query, chatList, fuse, setFilteredList])

	useEffect(() => {
		setFuse(
			new Fuse(chatList ?? [], {
				keys: ['title'],
			}),
		)
		// @ts-ignore
		setFilteredList(chatList)
	}, [chatList])

	const showAndFetchChat = async (chatId: number) => {
		closeSheet()
		dispatch('change-page', { page: 1 })
		await loadChatAsActiveChat(chatId)
	}

	return (
		<>
			{filteredList === undefined || (filteredList !== null && filteredList.length <= 0) ? (
				<div className="flex justify-center">
					<small>{t('No results')}</small>
				</div>
			) : (
				<>
					<div className="flex">
						<div className="flex mx-1 w-10"></div>
						<div className="grow m-2 ml-0 text-sm">{t('Title')}</div>
					</div>
					{filteredList === null
						? [1, 2, 3, 4, 5, 6, 7, 8].map((x) => <WordInListSkeleton key={x} mode="full" />)
						: filteredList.map((chat, idx) => (
								<div
									className="flex justify-between min-h-9"
									key={idx}
									onClick={() => showAndFetchChat(chat?.item?.id ?? chat?.id)}>
									<div className="flex items-center justify-center mx-1 w-10">
										<span className="text-xs">{idx + 1}</span>
									</div>
									<div className="flex flex-1 items-center text-sm">
										{chat?.item?.title ?? chat?.title}
									</div>
								</div>
							))}
				</>
			)}
		</>
	)
}

const VocabularySearch = ({ vocabularyList, query }: { vocabularyList: LibraryVocabulary[] | null; query: string }) => {
	const { t } = useTranslation()
	const [fuse, setFuse] = useState<Fuse<LibraryVocabulary>>()
	const [filteredList, setFilteredList] = useState<SearchResult<LibraryVocabulary>[] | null>(null)

	useEffect(() => {
		if (query === '') {
			// @ts-ignore
			setFilteredList(vocabularyList)
		} else {
			// @ts-ignore
			setFilteredList(fuse?.search(query))
		}
	}, [query, vocabularyList, fuse, setFilteredList])

	useEffect(() => {
		setFuse(
			new Fuse(vocabularyList ?? [], {
				keys: ['word', 'translation'],
			}),
		)
		// @ts-ignore
		setFilteredList(vocabularyList)
	}, [vocabularyList])

	return (
		<>
			{filteredList === undefined || (filteredList !== null && filteredList.length <= 0) ? (
				<div className="flex justify-center">
					<small>{t('No results')}</small>
				</div>
			) : (
				<>
					<div className="flex">
						<div className="flex mx-1 w-10"></div>
						<div className="flex flex-none m-2 w-11 text-sm justify-center">{t('Status')}</div>
						<div className="grow m-2 text-sm">{t('Word / Term')}</div>
					</div>
					{filteredList === null
						? [1, 2, 3, 4, 5, 6, 7, 8].map((x) => <WordInListSkeleton key={x} mode="full" />)
						: filteredList.map((libVoc, idx) => (
								<WordInList
									key={idx}
									index={++idx}
									libraryVocabulary={libVoc.item ?? libVoc}
									mode="full"
								/>
							))}
				</>
			)}
		</>
	)
}

const ItemsSheet = ({
	onClose,
	vocabularyList,
	chatsList = null,
	sideActionEnd = null,
	sideActionStart = null,
}: {
	onClose: () => void
	vocabularyList: LibraryVocabulary[] | null
	chatsList?: Chat[] | null
	sideActionEnd?: ReactNode
	sideActionStart?: ReactNode
}) => {
	const { t } = useTranslation()
	const { appLanguageCode, availableAppLanguages } = useGlobalStore()
	const [query, setQuery] = useState('')
	const searchInputRef = useRef(null)
	const [chosenCategory, setChosenCategory] = useState<string | null>('vocabulary')

	// const focusSearchInput = () => {
	// 	if (searchInputRef?.current !== null) {
	// 		// @ts-ignore
	// 		searchInputRef?.current.focus()
	// 	} else {
	// 		setQuery('')
	// 	}
	// }

	const resetOrCloseSheet = () => {
		if (query.length > 0) {
			// focusSearchInput()
			setQuery('')
		} else {
			onClose()
		}
	}

	useEffect(() => {
		setChosenCategory('vocabulary')
	}, [vocabularyList])

	return (
		<SheetContent
			dir={availableAppLanguages[appLanguageCode]?.dir}
			// onAnimationEnd={focusSearchInput}
			showClose={false}
			side="bottom"
			className="flex flex-col h-[80%] px-1 pb-0 pt-1 gap-2">
			<VisuallyHidden.Root>
				<DialogTitle>All vocabularies list</DialogTitle>
				<SheetDescription>All vocabularies list</SheetDescription>
			</VisuallyHidden.Root>
			<SheetHeader>
				{chatsList !== null && (
					<div className="inline-flex w-full items-center justify-center mt-2 -space-x-px rounded-md shadow-xs rtl:space-x-reverse">
						{sideActionStart}
						<Button
							onClick={() => setChosenCategory('vocabulary')}
							variant={chosenCategory === 'vocabulary' ? 'default' : 'outline'}
							className="rounded-none rounded-s-md shadow-none focus-visible:z-10 h-7 text-xs">
							{t('Vocabulary')}
						</Button>
						<Button
							onClick={() => setChosenCategory('chats')}
							variant={chosenCategory === 'chats' ? 'default' : 'outline'}
							className="rounded-none rounded-e-md shadow-none focus-visible:z-10 h-7 text-xs">
							{t('Chats')}
						</Button>
						{sideActionEnd}
					</div>
				)}
				<div className="flex flex-row items-center !gap-0">
					<Button
						disabled
						className="flex touch-none focus-visible:outline-none focus-visible:border-none focus:outline-none outline-none border-none"
						variant="ghost"
						size="icon">
						<Search className="h-4 w-4" />
					</Button>
					{/*{ query !== '' && <Button onClick={() => setQuery('')} className="flex focus-visible:outline-none focus-visible:border-none focus:outline-none outline-none border-none" variant="ghost" size="icon">*/}
					{/*	<Undo2 className="h-4 w-4" />*/}
					{/*</Button> }*/}
					<Input
						ref={searchInputRef}
						tabIndex={-1}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						type="text"
						className="gap-0 focus-visible:ring-offset-0 !m-0 outline-none !border-none focus-visible-ring-offset-0 focus-ring-0 focus-visible:ring-0 focus-visible:border-none"
						placeholder={t('Start typing...')}
					/>
					<Button
						className="flex touch-none focus-visible:outline-none focus-visible:border-none focus:outline-none outline-none border-none !m-0 p-3"
						variant="ghost"
						onClick={() => resetOrCloseSheet()}>
						<X className="h-4 w-4" />
						<span className="sr-only">Close</span>
					</Button>
				</div>
			</SheetHeader>
			<div className="overflow-auto">
				{chosenCategory === 'vocabulary' ? (
					<VocabularySearch vocabularyList={vocabularyList || []} query={query} />
				) : (
					<ChatSearch chatList={chatsList || []} query={query} closeSheet={onClose} />
				)}
			</div>
		</SheetContent>
	)
}

export { WordInListSkeleton, ItemsSheet }
